import * as Sentry from '@sentry/react-native';
import Constants from 'expo-constants';
import * as Notifications from 'expo-notifications';
import { ComponentType } from 'react';

import { config } from '~/app/config';
import { appSessionTimeout } from '~/app/constants';
import { AppEndpoint } from '~/contexts/app-config';
import { AppNavigationContainerRef } from '~/navigator/app-navigator';
import { getDebugMode, isNative, isProductionOrStaging } from '~/utils';

export type ErrorTags = {
    [key: string]: ID | number | string | boolean | null;
};

const reactNavigationIntegration = Sentry.reactNavigationIntegration();
const profile: AppEndpoint = Constants.expoConfig?.extra?.profile;

export const ignoredErrorMessages: string[] = [
    'Authentication error: refresh token expired', //thrown from auth context
    'Auth client token refresh failed: CombinedGraphQLErrors: Refresh token is expired',
    'Auth client token refresh failed: CombinedGraphQLErrors: Invalid refresh token',
    'Keskeytit tunnistuksen', // when member interrupts eident flow
    'Auth client e-ident verify failed: CombinedGraphQLErrors: No eIdent session found',
    // Client network errors
    'Network request failed',
    'Load failed',
    'Failed to fetch',
    'NetworkError when attempting to fetch resource',
    'Network service unavailable',
    // These are caused by browser extensions on mobile safari
    'runtime.sendMessage. Tab not found',
    "Can't find variable: observer",
    "undefined is not an object (evaluating 'response.showSearchResults')",
    // Next three happen when user navigates away while requests are in progress
    'AbortError',
    'The operation was aborted',
    'The user aborted a request'
];

/**
 * Initializes error diagnostics (Sentry). Called on app boot by the service notice
 * provider once it is known that the service is still available; all other helpers in
 * this module are safe no-ops until this has been called.
 */
export function initializeErrorDiagnostics() {
    if (!isProductionOrStaging()) {
        return;
    }

    Sentry.init({
        environment: profile,
        release: Constants.expoConfig?.version,
        dsn: config[profile]?.sentry?.dsn!,
        tracesSampleRate: 0.01,
        enableAutoSessionTracking: true,
        sessionTrackingIntervalMillis: appSessionTimeout,
        enableAppStartTracking: true,
        enableNativeFramesTracking: true,
        tracePropagationTargets: ['localhost', /.*.heltti.fi/, /^\//],
        integrations: [
            reactNavigationIntegration,
            Sentry.breadcrumbsIntegration({
                console: false,
                fetch: true,
                xhr: true
            })
        ],
        ignoreErrors: ignoredErrorMessages,
        beforeBreadcrumb: event => {
            if (event?.category === 'touch') {
                if (
                    event?.data?.componentTree?.find((val: string) => {
                        return val.startsWith('pin-code-keyboard');
                    })?.length > 0
                ) {
                    // message data for pin-code-keyboard identified users pin-code,
                    // thus it is removed from the sentry data
                    delete event.message;
                }
            }
            return event;
        }
    });
}

/**
 * Stops error diagnostics (Sentry) and flushes pending events. Called when the service
 * notice validity has passed and all network traffic must stop.
 */
export function closeErrorDiagnostics() {
    return Sentry.close();
}

export function registerNavigationContainer(navigation: AppNavigationContainerRef | null) {
    reactNavigationIntegration?.registerNavigationContainer(navigation);
}

export function registerUser(userId: ID | null) {
    Sentry.setUser(userId ? { id: userId } : null);
}

export function setRequestId(requestId: UUID) {
    Sentry.setTag('request_id', requestId);
}

export function startSpan(...args: Parameters<typeof Sentry.startSpan>) {
    return Sentry.startSpan(...args);
}

export function log(message: string, options: { notify: boolean } = { notify: false }) {
    Sentry.addBreadcrumb({ message, level: 'log' });

    if (isNative() && getDebugMode() && options?.notify) {
        Notifications.scheduleNotificationAsync({ trigger: null, content: { title: 'Debug', body: message } }).finally(
            () => {}
        );
    }

    if (__DEV__ && typeof jest === 'undefined') {
        // eslint-disable-next-line no-console
        console.log(message);
    }
}

export function info(message: string) {
    if (__DEV__ && typeof jest === 'undefined') {
        // eslint-disable-next-line no-console
        console.log(message);
    }
    Sentry.captureMessage(message, 'info');
}

export function warning(message: string) {
    if (__DEV__ && typeof jest === 'undefined') {
        // eslint-disable-next-line no-console
        console.warn(message);
    }
    Sentry.captureMessage(message, 'warning');
}

export function error(err: unknown, tags?: ErrorTags) {
    if (__DEV__ && typeof jest === 'undefined') {
        // eslint-disable-next-line no-console
        console.log(err);
    }
    Sentry.captureException(err, { tags });
}

export function withErrorDiagnostics(component: ComponentType) {
    return Sentry.wrap(component, {
        touchEventBoundaryProps: { ignoreNames: ['View', 'Pressable', 'StyledNativeComponent', 'Svg', 'G', 'Path'] }
    });
}
